import { useState, useRef, useEffect } from "react";
import { UpdateUserData, User } from "@/hooks/useUsersApi";
import { createApiService } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { toastHandler } from "@/lib/ToastHandler";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/atoms/dialog";
import { Input } from "@/components/atoms/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/select";
import { Button } from "@/components/atoms/button";
import { Info, Pencil, CircleX, X, ChevronDown, Search } from "lucide-react";
import InvitationStatusMessage from "./InvitationStatusMessage";

interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onUserUpdated?: (updatedUser: User) => void;
  brands?: any[]; // Marcas pasadas desde el componente padre
}

export default function UserEditModal({
  isOpen,
  onClose,
  user,
  onUserUpdated,
  brands = [], // Usar las marcas pasadas como prop, con fallback a array vacío
}: UserEditModalProps) {
  const { getAccessToken } = useAuth();

  // Update user function without using the full hook
  const updateUser = async (id: number, userData: UpdateUserData) => {
    try {
      const token = await getAccessToken();
      const usersService = createApiService<User>("/auth/users", () =>
        Promise.resolve(token),
      );
      const updatedUser = await usersService.update(id, userData);
      toastHandler({
        description: `Usuario actualizado exitosamente`,
        title: "Éxito",
        variant: "success",
      });
      return updatedUser;
    } catch (error) {
      console.error("Error updating user:", error);
      toastHandler({
        description:
          error instanceof Error
            ? error.message
            : "Error al actualizar el usuario. Intente nuevamente.",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusType, setStatusType] = useState<"success" | "error" | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [fullName, setFullName] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedBusinesses, setSelectedBusinesses] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);

  // Role mapping: UI display name -> API value
  const roleMapping = {
    Encargado: "manager",
    Administrador: "admin",
    Manager: "manager",
    Admin: "admin",
  };

  const roles = ["Encargado", "Administrador"];

  // Original values for comparison
  const [originalFullName, setOriginalFullName] = useState("");
  const [originalRole, setOriginalRole] = useState("");
  const [originalBusinesses, setOriginalBusinesses] = useState<string[]>([]);

  const [businessSearchTerm, setBusinessSearchTerm] = useState<string>("");
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] =
    useState<boolean>(false);
  const businessDropdownRef = useRef<HTMLDivElement>(null);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        businessDropdownRef.current &&
        !businessDropdownRef.current.contains(event.target as Node)
      ) {
        setIsBusinessDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Load user data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      const fullName = `${user.first_name} ${user.last_name}`;
      const role =
        user.role === "manager"
          ? "Encargado"
          : user.role === "admin"
            ? "Administrador"
            : user.role || "";
      const businesses = user.brand_ids?.map((id) => id.toString()) || [];

      setFullName(fullName);
      setSelectedRole(role);
      setSelectedBusinesses(businesses);

      // Store original values for comparison
      setOriginalFullName(fullName);
      setOriginalRole(role);
      setOriginalBusinesses(businesses);

      // Reset other states
      setErrors({});
      setStatusType(null);
      setErrorMessage("");
      setBusinessSearchTerm("");
      setIsBusinessDropdownOpen(false);
    }
  }, [isOpen, user]);

  // Reset form when modal closes
  const resetForm = () => {
    setFullName("");
    setSelectedRole("");
    setSelectedBusinesses([]);
    setOriginalFullName("");
    setOriginalRole("");
    setOriginalBusinesses([]);
    setBusinessSearchTerm("");
    setIsBusinessDropdownOpen(false);
    setErrors({});
    setStatusType(null);
    setErrorMessage("");
    setIsSubmitting(false);
    setHasBeenSubmitted(false);
  };

  // Handle business selection (multiple for Encargado)
  const handleBusinessChange = (brandId: string) => {
    const newSelectedBusinesses = selectedBusinesses.includes(brandId)
      ? selectedBusinesses.filter((id) => id !== brandId)
      : [...selectedBusinesses, brandId];

    setSelectedBusinesses(newSelectedBusinesses);

    // Clear search term after selection
    setBusinessSearchTerm("");

    // Clear error when user selects
    if (errors.business) {
      setErrors((prev) => ({ ...prev, business: "" }));
    }
  };

  // Handle role change
  const handleRoleChange = (role: string) => {
    setSelectedRole(role);

    // Reset business selections when role changes to Administrador
    if (role === "Administrador") {
      setSelectedBusinesses([]);
    }

    // Clear error when user selects
    if (errors.role) {
      setErrors((prev) => ({ ...prev, role: "" }));
    }
  };

  // Filter brands based on search term
  const filteredBrands = brands.filter((brand) =>
    brand.name.toLowerCase().includes(businessSearchTerm.toLowerCase()),
  );

  // Handle business search input
  const handleBusinessSearch = (value: string) => {
    setBusinessSearchTerm(value);
    setIsBusinessDropdownOpen(true);

    // Clear error when user starts typing
    if (errors.business) {
      setErrors((prev) => ({ ...prev, business: "" }));
    }
  };

  // Handle business selection
  const handleBusinessSelect = (brandId: string) => {
    // Don't allow selecting already selected businesses
    if (selectedBusinesses.includes(brandId)) {
      return;
    }

    handleBusinessChange(brandId);
    setBusinessSearchTerm("");
    setIsBusinessDropdownOpen(false);
  };

  // Full name validation
  const validateFullName = (name: string): string => {
    // Verificar que no esté vacío
    if (!name.trim()) {
      return "El nombre completo es requerido";
    }

    // Verificar que no tenga espacios al inicio o final
    if (name !== name.trim()) {
      return "El nombre no puede tener espacios al inicio o final";
    }

    // Verificar que tenga al menos dos palabras (nombre y apellido)
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length < 2) {
      return "Debe ingresar al menos nombre y apellido";
    }

    // Verificar que cada parte tenga al menos 2 caracteres
    const hasValidParts = nameParts.every((part) => part.length >= 2);
    if (!hasValidParts) {
      return "Cada nombre debe tener al menos 2 caracteres";
    }

    return ""; // Sin errores
  };

  // Split full name into first_name and last_name
  const splitFullName = (fullName: string) => {
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    return { firstName, lastName };
  };

  // Check if there are any changes
  const hasChanges = () => {
    const nameChanged = fullName.trim() !== originalFullName.trim();
    const roleChanged = selectedRole !== originalRole;
    const businessesChanged =
      JSON.stringify(selectedBusinesses.sort()) !==
      JSON.stringify(originalBusinesses.sort());

    return nameChanged || roleChanged || businessesChanged;
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate full name
    if (!fullName.trim()) {
      newErrors.fullName = "El nombre completo es requerido";
    } else {
      const nameError = validateFullName(fullName);
      if (nameError) {
        newErrors.fullName = nameError;
      }
    }

    if (!selectedRole) {
      newErrors.role = "El rol es requerido";
    }

    // Only validate business for Encargado role
    if (selectedRole === "Encargado") {
      if (selectedBusinesses.length === 0) {
        newErrors.business = "Al menos una marca es requerida";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !validateForm() ||
      !hasChanges() ||
      !user ||
      isSubmitting ||
      hasBeenSubmitted
    ) {
      return;
    }

    // Split full name before submitting
    const { firstName, lastName } = splitFullName(fullName);

    // Convert role from Spanish to English for API
    const apiRole =
      roleMapping[selectedRole as keyof typeof roleMapping] ||
      selectedRole.toLowerCase();

    const updateData: UpdateUserData = {};

    // Only include changed fields
    if (fullName.trim() !== originalFullName.trim()) {
      updateData.first_name = firstName;
      updateData.last_name = lastName;
    }

    if (selectedRole !== originalRole) {
      updateData.role = apiRole;
    }

    // Handle brand_ids changes
    const businessesChanged =
      JSON.stringify(selectedBusinesses.sort()) !==
      JSON.stringify(originalBusinesses.sort());

    if (businessesChanged) {
      if (selectedRole === "Administrador") {
        updateData.brand_ids = []; // Admins have access to all brands
      } else {
        updateData.brand_ids = selectedBusinesses.map((id) => parseInt(id));
      }
    }

    // IMPORTANTE: Si solo se están cambiando las brands y el usuario ya es manager,
    // incluir también el rol para evitar el error del servidor
    if (
      businessesChanged &&
      selectedRole === "Encargado" &&
      originalRole === "Encargado"
    ) {
      updateData.role = apiRole; // Asegurar que el rol esté incluido
    }

    // Si estamos cambiando el rol Y las brands, siempre incluir ambos en la misma llamada
    // Esto evita problemas de sincronización con el servidor
    if (selectedRole !== originalRole && businessesChanged) {
      if (selectedRole === "Administrador") {
        updateData.brand_ids = [];
      } else {
        updateData.brand_ids = selectedBusinesses.map((id) => parseInt(id));
      }
    }

    setIsSubmitting(true);
    setHasBeenSubmitted(true);

    try {
      const updatedUser = await updateUser(user.id, updateData);

      // Crear el objeto completo del usuario actualizado para mostrar en la UI
      const brandIds =
        selectedRole === "Administrador"
          ? []
          : selectedBusinesses.map((id) => parseInt(id));
      const fullUpdatedUser: User = {
        ...user,
        // Aplicar las actualizaciones del servidor
        ...updatedUser,
        // Forzar los valores locales para asegurar consistencia inmediata
        first_name: fullName.trim().split(" ")[0] || user.first_name,
        last_name:
          fullName.trim().split(" ").slice(1).join(" ") || user.last_name,
        role:
          roleMapping[selectedRole as keyof typeof roleMapping] ||
          selectedRole.toLowerCase(),
        brand_ids: brandIds,
        // Crear el formato brands para compatibilidad
        brands: brandIds.map((id) => {
          const brand = brands.find((b) => b.id === id);
          return brand
            ? { id: brand.id, name: brand.name }
            : { id, name: `Brand ${id}` };
        }),
      };

      if (onUserUpdated) {
        onUserUpdated(fullUpdatedUser);
      }
      setStatusType("success");
    } catch (error) {
      console.error("Error updating user:", error);
      const errorMsg =
        error instanceof Error
          ? error.message
          : "Error desconocido al actualizar el usuario";
      setErrorMessage(errorMsg);
      setStatusType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(v: boolean) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent className="max-w-md w-full p-0 rounded-2xl overflow-hidden bg-white [&>button]:hidden max-h-[90vh] sm:max-h-[600px] overflow-y-auto">
        {!statusType ? (
          <form onSubmit={handleSubmit} className="flex flex-col">
            <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b relative">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl border border-gray-200 flex items-center justify-center">
                  <Pencil className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                </div>
                <div>
                  <DialogTitle className="text-base sm:text-lg font-medium">
                    Editar usuario
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-500">
                    Modifica la información del usuario
                  </DialogDescription>
                </div>
              </div>
              {/* Custom close button */}
              <button
                type="button"
                onClick={handleClose}
                className="absolute right-4 sm:right-6 top-3 sm:top-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <CircleX className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </DialogHeader>

            <div className="px-4 sm:px-6 py-3 sm:py-4 space-y-3 sm:space-y-4">
              {/* Email (read-only) */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <Input
                  type="email"
                  value={user.email}
                  disabled
                  className="h-8 sm:h-10 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>

              {/* Full Name */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Nombre completo
                </label>
                <Input
                  type="text"
                  placeholder="Anna Taylor"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (errors.fullName) {
                      setErrors((prev) => ({ ...prev, fullName: "" }));
                    }
                  }}
                  onBlur={() => {
                    if (fullName.trim()) {
                      const nameError = validateFullName(fullName);
                      if (nameError) {
                        setErrors((prev) => ({ ...prev, fullName: nameError }));
                      }
                    }
                  }}
                  className={`h-8 sm:h-10 text-sm ${errors.fullName ? "border-red-300" : ""}`}
                />
                {errors.fullName && (
                  <p className="text-xs text-red-600 mt-0.5">
                    {errors.fullName}
                  </p>
                )}
              </div>

              {/* Role */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Rol
                </label>
                <Select value={selectedRole} onValueChange={handleRoleChange}>
                  <SelectTrigger
                    className={`h-8 sm:h-10 text-sm ${errors.role ? "border-red-300" : ""}`}
                  >
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role} value={role} className="text-sm">
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.role && (
                  <p className="text-xs text-red-600 mt-0.5">{errors.role}</p>
                )}
              </div>

              {/* Admin info alert */}
              {selectedRole === "Administrador" && (
                <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Info className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-green-800">
                    Los administradores tienen acceso a todas las marcas por
                    defecto.
                  </p>
                </div>
              )}

              {/* Business - Multiple Selection for Encargado only */}
              {selectedRole === "Encargado" && (
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Marcas
                  </label>
                  <div className="relative" ref={businessDropdownRef}>
                    <button
                      type="button"
                      onClick={() =>
                        setIsBusinessDropdownOpen(!isBusinessDropdownOpen)
                      }
                      className={`flex h-8 sm:h-10 w-full items-center justify-between whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 sm:px-4 py-1 sm:py-2 text-sm shadow-sm ring-offset-background transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.business
                          ? "border-red-300 focus:border-red-300 focus:ring-red-200"
                          : ""
                      }`}
                    >
                      <span className="text-gray-500">
                        {businessSearchTerm || "Seleccionar marca"}
                      </span>
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    </button>

                    {isBusinessDropdownOpen && (
                      <div
                        className="absolute z-50 max-h-[200px] sm:max-h-[300px] min-w-[8rem] overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-900 shadow-lg animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 w-full"
                        style={{
                          bottom: "100%",
                          marginBottom: "4px",
                        }}
                      >
                        <div className="p-2 pb-0">
                          <div className="relative mb-2">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              type="text"
                              placeholder="Buscar marca..."
                              value={businessSearchTerm}
                              onChange={(e) =>
                                handleBusinessSearch(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                }
                              }}
                              className="pl-9 h-8 sm:h-10 text-sm"
                              autoFocus
                            />
                          </div>
                        </div>

                        <div className="max-h-[150px] sm:max-h-[200px] overflow-y-auto px-2 pb-2">
                          {filteredBrands.length > 0 ? (
                            filteredBrands.map((brand) => {
                              const isSelected = selectedBusinesses.includes(
                                brand.id.toString(),
                              );
                              return (
                                <div
                                  key={brand.id}
                                  onClick={() =>
                                    handleBusinessSelect(brand.id.toString())
                                  }
                                  className={`relative flex w-full select-none items-center rounded-md py-1.5 sm:py-2 pl-3 pr-9 text-sm outline-none transition-colors ${
                                    isSelected
                                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                      : "cursor-pointer hover:bg-blue-50 focus:bg-blue-100 focus:text-blue-900"
                                  }`}
                                >
                                  {brand.name}
                                  {isSelected && (
                                    <span className="absolute right-3 flex h-4 w-4 items-center justify-center">
                                      <X className="h-3 w-3 text-gray-400" />
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              {businessSearchTerm
                                ? "No se encontraron marcas"
                                : "Escribe para buscar..."}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selected Businesses Tags */}
                  {selectedBusinesses.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-1.5 sm:mt-2">
                      {selectedBusinesses.map((brandId) => {
                        const business = brands.find(
                          (b) => b.id.toString() === brandId,
                        );
                        return business ? (
                          <div
                            key={brandId}
                            className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                          >
                            <span>{business.name}</span>
                            <button
                              type="button"
                              onClick={() => handleBusinessChange(brandId)}
                              className="hover:bg-blue-200 rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}

                  {errors.business && (
                    <p className="text-xs text-red-600 mt-0.5">
                      {errors.business}
                    </p>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="bg-white border-t flex justify-end gap-2 px-4 sm:px-6 py-3 sm:py-4">
              <Button
                variant="outline"
                onClick={handleClose}
                type="button"
                disabled={isSubmitting}
                className="h-8 sm:h-10 text-sm border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !hasChanges()}
                className="h-8 sm:h-10 text-sm bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Actualizando..." : "Confirmar"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <InvitationStatusMessage
            type={statusType}
            email={user.email}
            errorMessage={errorMessage}
            onClose={handleClose}
            mode="permissions_update"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
